import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Profile } from 'src/app/domain/cabinet/dto/Profile';
import { CabinetService } from 'src/app/domain/cabinet/service/cabinet.service';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { OrganizationsService } from 'src/app/domain/organization/components/service/organizations.service';
import { UploadComponent } from '../../../shared/component/upload/upload.component';
import { PhoneService } from 'src/app/domain/login/service/phone.service';
import { VerificationMethod } from 'src/app/domain/login/component/dto/login.dto';
import { OrganizationWithAccesses } from 'src/app/core/component/hornavbar/dto/InitDataDTO';
import { ItsTheFinalCountdown, SUCCESS_TEXT_NOTIFICATIONS } from './cabinetExportClasses';

@Component({
  selector: 'app-cabinet',
  templateUrl: './cabinet.component.html',
  styleUrls: ['./cabinet.component.scss'],
})
export class CabinetComponent implements OnInit, OnDestroy {
  @ViewChild('upload') uploadComponent: UploadComponent;
  private _destroyStream: Subject<void> = new Subject<void>();
  profile: Profile;
  accessList: OrganizationWithAccesses[] = [];


  title = 'imageuploader';

  updateProfileForm: FormGroup;

  // PHONE NUMBER INPUT //////////////////
  show = new ShowChangePhoneAndPasswordElements();
  counter = new ItsTheFinalCountdown();
  notificationCounter = 0;

  verifyNewPhoneData: VerifyNewPhoneWrapper = {
    newPhone: '',
    confirmationCode: '',
  };

  verificationMethods = { sms: 'SMS', whatsapp: 'WhatsApp' };
  verificationMethodsValues = Object.values(this.verificationMethods);

  errorImageLink = ERROR_IMAGE_LINK;
  numberDescription = NUMBER_DESCRIPTION;

  constructor(
    private service: CabinetService,
    private notificationsService: NotificationsService,
    private formBuilder: UntypedFormBuilder,
    private organizationService: OrganizationsService,
    private phoneService: PhoneService,
  ) { }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngOnInit(): void {
    this.organizationService.all$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((orgs) => {
        if (!orgs) {
          return;
        }
        this.accessList = this.organizationService.allOrgsWithAccess;
      });
    this.service.profile
      .pipe(takeUntil(this._destroyStream))
      .subscribe((profile) => {
        if (!profile) return;
        this.profile = profile;
        this.createForm();
      });
  }

  async updateProfile(): Promise<void> {
    const awsImageName = await this.uploadComponent.upload('profile', this.profile.id + '.jpeg');
    if (awsImageName) {
      this.updateProfileForm.patchValue({ photo: awsImageName });
    }
    const res = await this.service.updateProfile(this.updateProfileForm.value);
    res.phone = this.profile.phone;
    res.verificationMethod = this.profile.verificationMethod;
    if (res) {
      this.profile = res;
      this.show.safeInfoButton = false;
      this.service.setProfile(res);
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    }
  }

  // CHANGE PHONE NUMBER //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Check for 1) not allowed symbols 2) not changed number 3)changes after SMS was sent
   * Temporary safe changed phone from input field and show `Send code` button instead of description.
   * `this.verifyNewPhoneData.newPhone` global var for backEnd request
   *
   * @param editedPhoneNumber changed phone from input field
   */

  editPhone(editedPhoneNumber: string): void {
    if (!/^\+?\d+$/.test(editedPhoneNumber)) {
      if (this.notificationCounter < 1) {
        this.notificationsService.error(NOTIFICATIONS.PHONE_CONTAINS_NOT_ALLOWED_SYMBOLS_ERROR);
        this.notificationCounter++;
      }
      this.show.sendCodeButton = false;
      this.show.errorPhoneInput = true;
      return;
    }

    if (this.profile.phone === this.updateProfileForm.value.phone.toString()) {
      this.notificationCounter = 0;
      this.refreshShowParameters();
      return;
    }

    if (this.updateProfileForm.value.phone && this.show.codeInput) {
      this.refreshShowParameters();
    }

    this.notificationCounter = 0;
    this.show.newPhoneChecksPassed();
    this.verifyNewPhoneData.newPhone = editedPhoneNumber;
    this.profile.phone = this.updateProfileForm.value.phone;
  }


  /** Change verification method, currently 'sms' and 'whatsapp', check if it not the same.
   * `newVerificationMethod` transform 'SMS' to 'sms
   *
   * @param editedVerifyMethod get new verification Method e.g 'SMS'
   */

  changeVerificationMethod(editedVerifyMethod: string): void {
    const newVerificationMethod = Object.keys(this.verificationMethods).find((key) =>
      this.verificationMethods[key] === editedVerifyMethod) as VerificationMethod;

    if (newVerificationMethod !== this.profile.verificationMethod) {
      try {
        this.phoneService.changeVerificationMethod(newVerificationMethod);
        this.showSuccessNotification('change', newVerificationMethod);
        this.profile.verificationMethod = newVerificationMethod;
      } catch (err) {
        this.notificationsService.error(NOTIFICATIONS.FORBIDDEN_ERROR);
      }
    }
  }

  sendVerificationCode(): void {
    this.show.timerAndCodeInput();
    this.showSuccessNotification('send', this.profile.verificationMethod);
    this.startTimerForNextSms();
    this.phoneService.sendVerificationCode(this.verifyNewPhoneData.newPhone, this.profile.verificationMethod);
  }

  enterVerificationCode(): void {
    const enteredcode = this.updateProfileForm.value.verificationCode;
    if (!/^[0-9]+$/.test(enteredcode)) {
      this.show.errorCodeInput = true;
      return;
    }
    this.show.errorCodeInput = false;
  }

  /** When `Submit` button is clicked check for correct code.
   * Send backend request with new number and code and verify code, if correct `refreshShowParameters()`*/

  async submitVerificationCode(): Promise<void> {
    this.verifyNewPhoneData.confirmationCode = this.updateProfileForm.value.verificationCode;
    try {
      await this.phoneService.changePhone(this.verifyNewPhoneData);
      this.refreshShowParameters();
      this.updateProfileForm.patchValue({ verificationCode: '' });
      this.notificationsService.success(NOTIFICATIONS.SET_NEW_PHONE_NUMBER_SUCCESS);

    } catch (err) {
      const x = { title: 'Invalid code', message: 'Please check your code and try again. ' };
      x.message += (this.counter.attemptsCounter) + ' attempts left';
      this.show.errorCodeInput = true;
      this.counter.attemptsCounter--;
      this.notificationsService.error(this.counter.attemptsCounter >= 0 ? x : NOTIFICATIONS.MAX_ATTEMPTS_REACHED);
    }
  }

  /** Starts 30seconds timer. At the end of it shows `Resend` button. If
   * number was successfully updated stops the timer*/

  startTimerForNextSms(): void {
    const thirtySeconds = 30000; // in milliseconds
    const countdownDate = new Date().getTime() + thirtySeconds;
    this.counter.countdownSeconds = 30;

    this.counter.countdownInterval = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownDate - now;
      this.counter.countdownSeconds = Math.floor((distance % (1000 * 30)) / 1000); // get seconds;

      if (distance < 0) {
        clearInterval(this.counter.countdownInterval);
        this.counter.countdownInterval = null;
        this.counter.countdownSeconds = 0;

        this.show.timer = false;
        if (this.show.description || this.show.sendCodeButton) {
          this.show.resendCodeButton = false;
          return;
        }
        this.show.resendCodeButton = true;

      }
    }, 1000);
  }

  showSuccessNotification(type: 'send' | 'change', verificationMethod: VerificationMethod): void {
    this.notificationsService.success(NOTIFICATIONS[SUCCESS_TEXT_NOTIFICATIONS[type][verificationMethod]]);
  }

  refreshShowParameters(): void {
    this.show = new ShowChangePhoneAndPasswordElements();
  }

  // CHANGE PASSWORD //////////////////////////////////////////////////////////////////////////////////////////////////
  showModalChangePwd(): void {
    this.show.changePwdModal = true;
  }

  hideModalChangePwd(): void {
    this.show.changePwdModal = false;
  }

  private createForm(): void {
    this.updateProfileForm = this.formBuilder.group(
      {
        id: new FormControl(this.profile?.id),
        name: new FormControl(this.profile?.name, { validators: [Validators.required, Validators.minLength(1) ], updateOn: 'change' }),
        secondName: new FormControl(this.profile?.secondName, { updateOn: 'change' }),
        company: new FormControl(this.profile?.company, { updateOn: 'change' }),
        email: new FormControl({ value: this.profile?.email, disabled: true }),
        linkedIn: new FormControl(this.profile?.linkedIn, { updateOn: 'change' }),
        position: new FormControl(this.profile?.position, { updateOn: 'change' }),
        phone: new FormControl(this.profile?.phone, {
          validators: [Validators.required, Validators.pattern(/^\+?\d+$/)],
          updateOn: 'change',
        }),
        photo: new FormControl(this.profile?.photo, { updateOn: 'change' }),
        verificationCode: new FormControl(this.verifyNewPhoneData.confirmationCode, { updateOn: 'change' }),
      });
    this.updateProfileForm.valueChanges.subscribe(() => {
      this.show.safeInfoButton = this.updateProfileForm.get('phone').value === this.profile.phone;
    });
  }
}

export const ERROR_IMAGE_LINK = 'https://servcfilesystem.s3.eu-central-1.amazonaws.com/forDevs/147f3e00-4d65-43f1-83f5-1f10ac484325.jfif';

export const NUMBER_DESCRIPTION = 'This number will be used to send you the 2FA code';

export class VerifyNewPhoneWrapper {
  newPhone: string;
  confirmationCode: string;
};

class ShowChangePhoneAndPasswordElements {
  sendCodeButton = false;
  resendCodeButton = false;
  timer = false;
  description = true;
  codeInput = false;
  errorPhoneInput = false;
  errorCodeInput = false;
  safeInfoButton = false;

  changePwdModal: boolean = false;

  newPhoneChecksPassed(): void {
    this.description = false;
    this.errorPhoneInput = false;
    this.sendCodeButton = true;
    this.resendCodeButton = false;
  }

  timerAndCodeInput(): void {
    this.codeInput = true;
    this.sendCodeButton = false;
    this.resendCodeButton = false;
    this.timer = true;
  };

};

/** https://www.youtube.com/watch?v=9jK-NcRmVcw */

